<template>
    <div class="page">
        <div class="header">
            <div class="header-inner">
                <div class="legend">
                    <h3>Afwezigheid</h3>
                    <div class="legend-items">
                        <div class="legend-item">
                            <div class="legend-color sick"></div>
                            <span>Ziek</span>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color vacation"></div>
                            <span>Vakantie</span>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color other"></div>
                            <span>Anders</span>
                        </div>
                        <div class="legend-item">
                            <div class="legend-dot"></div>
                            <span>Aanwezig</span>
                        </div>
                    </div>
                </div>

                <div class="header-controls">
                    <button @click="previousWeek" class="nav-button">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                d="M15.75 19.5L8.25 12l7.5-7.5"
                                stroke="currentColor"
                                fill="none"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>

                    <div class="week-range">{{ weekRange }}</div>

                    <button @click="nextWeek" class="nav-button">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                stroke="currentColor"
                                fill="none"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <Loading v-if="isLoading" />

        <div v-else class="content">
            <div class="calendar">
                <div class="calendar-header">
                    <div class="employee-cell">Medewerker</div>
                    <div v-for="day in weekDays" :key="day.date" class="day-cell">
                        <div class="day-name">{{ day.dayName }}</div>
                        <div class="day-date">{{ day.dateFormatted }}</div>
                    </div>
                </div>

                <div class="departments">
                    <div v-for="dept in departments" :key="dept.name" class="department">
                        <div
                            class="dept-header"
                            :class="{ 'is-open': isOpen(dept.name) }"
                            @click="toggleDept(dept.name)"
                        >
                            <div class="dept-left">
                                <svg
                                    class="chevron"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M7 5l6 5-6 5"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <span class="dept-name">{{ dept.name || 'No department' }}</span>
                            </div>

                            <div class="dept-right">
                                <span class="dept-count">{{ dept.employees.length }}</span>
                            </div>
                        </div>

                        <transition name="fade">
                            <div v-show="isOpen(dept.name)">
                                <div
                                    v-for="employee in dept.employees"
                                    :key="employee.fullName"
                                    class="employee-row"
                                >
                                    <div class="employee-info">
                                        <div class="employee-name">{{ employee.fullName }}</div>
                                        <div class="employee-dept">{{ employee.kostenplaats }}</div>
                                    </div>

                                    <div
                                        v-for="day in weekDays"
                                        :key="`${employee.fullName}-${day.date}`"
                                        class="status-cell"
                                    >
                                        <div
                                            v-if="getAbsenceForDay(employee.absences, day.date)"
                                            class="absence-box"
                                            :class="getAbsenceStyle(getAbsenceForDay(employee.absences, day.date))"
                                        >
                                            <div class="absence-title">
                                                {{ getAbsenceOmschrijving(employee.absences, day.date) }}
                                            </div>
                                            <div
                                                v-if="getAbsenceHours(employee.absences, day.date) !== null"
                                                class="absence-hours"
                                            >
                                                {{ getAbsenceHours(employee.absences, day.date) }}
                                            </div>
                                        </div>

                                        <div v-else class="no-absence">•</div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import Loading from '@/components/Loading'
import request from '@/functions/request.js'
import { mapState } from 'vuex'

export default {
    name: 'EmployeeAbsences',
    components: {
        Loading
    },
    data: () => ({
        currentWeekStart: moment().startOf('isoWeek'),
        absenceData: [],
        isLoading: false,
        error: null
    }),
    created() {
        this.getData()
    },
    computed: {
        ...mapState({
            expandedDepartments: state => state.departmentstates.expandedDepartments
        }),
        weekDays() {
            const days = []

            for (let i = 0; i < 7; i++) {
                const day = moment(this.currentWeekStart).add(i, 'days')
                days.push({
                    date: day.format('YYYY-MM-DD'),
                    dayName: day.format('ddd'),
                    dateFormatted: day.format('MMM D')
                })
            }

            return days
        },
        weekRange() {
            const start = moment(this.currentWeekStart)
            const end = moment(this.currentWeekStart).add(6, 'days')
            return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
        },
        groupedEmployees() {
            const employeeMap = new Map()

            this.absenceData.forEach((record) => {
                const fullName = `${record.Roepnaam} ${record.Voorvoegsel ? `${record.Voorvoegsel} ` : ''}${record.Achternaam}`
                const displayName = record.Roepnaam || record.Voornaam

                if (!employeeMap.has(fullName)) {
                    employeeMap.set(fullName, {
                        fullName,
                        displayName,
                        kostenplaats: record.Kostenplaats,
                        absences: []
                    })
                }

                employeeMap.get(fullName).absences.push(record)
            })

            return Array.from(employeeMap.values()).sort((a, b) => {
                if (a.kostenplaats !== b.kostenplaats) {
                    return a.kostenplaats.localeCompare(b.kostenplaats)
                }

                return a.displayName.localeCompare(b.displayName)
            })
        },
        departments() {
            const groups = {}

            this.groupedEmployees.forEach((employee) => {
                const key = employee.kostenplaats || 'Other'

                if (!groups[key]) {
                    groups[key] = []
                }

                groups[key].push(employee)
            })

            return Object.keys(groups)
                .sort()
                .map((name) => ({
                    name,
                    employees: groups[name]
                }))
        }
    },
    watch: {
        departments: {
            immediate: true,
            handler(groups) {
                if (Object.keys(this.expandedDepartments || {}).length > 0) return

                const currentState = {}
                groups.forEach((group) => {
                    currentState[group.name] = true
                })

                this.$store.dispatch('departmentstates/setExpandedDepartments', currentState)
            }
        }
    },
    methods: {
        async getData() {
            const start = moment(this.currentWeekStart).format('YYYYMMDD')
            const end = moment(this.currentWeekStart).add(5, 'days').format('YYYYMMDD')

            this.isLoading = true
            this.error = null

            try {
                const response = await request(`absence-registration/${start}/${end}`, 'GET')
                this.absenceData = Array.isArray(response.absence_registration)
                    ? response.absence_registration
                    : []
            } catch (error) {
                console.error('Error fetching absence data:', error)
                this.error = 'Kon gegevens niet laden.'
                this.absenceData = []
            } finally {
                this.isLoading = false
            }
        },
        async previousWeek() {
            this.currentWeekStart = moment(this.currentWeekStart).subtract(1, 'week')
            await this.getData()
        },
        async nextWeek() {
            this.currentWeekStart = moment(this.currentWeekStart).add(1, 'week')
            await this.getData()
        },
        getAbsenceForDay(absences, date) {
            return absences.find((absence) => {
                const start = moment(absence.Begindatum)
                const end = moment(absence.Einddatum)
                const checkDate = moment(date)

                return checkDate.isBetween(start, end, 'day', '[]')
            })
        },
        getAbsenceStyle(absence) {
            if (!absence) return ''

            const type = String(absence.Omschrijving || '').toLowerCase()

            if (type.includes('ziek')) return 'absence-sick'
            if (type.includes('verlof')) return 'absence-vacation'

            return 'absence-other'
        },
        getAbsenceOmschrijving(absences, date) {
            const absence = this.getAbsenceForDay(absences, date)
            return absence ? absence.Omschrijving : ''
        },
        getAbsenceHours(absences, date) {
            const absence = this.getAbsenceForDay(absences, date)

            if (
                absence &&
                this.convertTimeToSeconds(absence.Aantal_uren_orginal) > 0 &&
                absence.Aantal_uren < 8
            ) {
                return 'Dagdeel'
            }

            return 'Hele dag'
        },
        toggleDept(name) {
            this.$store.dispatch('toggleDepartment', name)
        },
        isOpen(name) {
            if (!this.expandedDepartments) return true

            const value = this.expandedDepartments[name]
            return value === undefined ? true : !!value
        },
        convertTimeToSeconds(timeString) {
            const hms = timeString.split(':')
            return (+hms[0]) * 60 * 60 + (+hms[1]) * 60 + (+hms[2])
        }
    }
}
</script>

<style scoped>
.page {
    min-height: 100vh;
    background: #f9fafb;
}

.header {
    background: white;
    border-bottom: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.header-inner {
    max-width: 1280px;
    margin: auto;
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title h1 {
    font-size: 1.5rem;
    font-weight: bold;
    color: #111827;
}

.header-title p {
    font-size: 0.875rem;
    color: #4b5563;
    margin-top: 0.25rem;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.nav-button {
    padding: 0.5rem;
    border-radius: 50%;
    transition: background 0.2s;
}

.nav-button:hover {
    background: #f3f4f6;
}

.nav-button svg {
    width: 20px;
    height: 20px;
    color: #4b5563;
}

.week-range {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
}

.content {
    max-width: 1280px;
    margin: auto;
    padding: 1.5rem;
}

.calendar {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.calendar-header {
    position: sticky;
    top: 50px;
    z-index: 20;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.employee-cell {
    padding: 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    border-right: 1px solid #e5e7eb;
    background: #f9fafb;
}

.day-cell {
    padding: 1rem;
    text-align: center;
    border-right: 1px solid #e5e7eb;
    background: #f9fafb;
}

.day-cell:last-child {
    border-right: none;
}

.day-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
}

.day-date {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
}

.departments {
    position: relative;
    z-index: 1;
}

.department {
    border-top: 1px solid #e5e7eb;
}

.dept-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: #f3f4f6;
    cursor: pointer;
    user-select: none;
}

.dept-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.dept-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.dept-name {
    font-weight: 600;
    color: #111827;
}

.dept-count {
    font-size: 0.75rem;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    background: #e5e7eb;
    color: #374151;
}

.chevron {
    width: 16px;
    height: 16px;
    transform: rotate(-90deg);
    transition: transform 0.2s ease;
    color: #4b5563;
}

.dept-header.is-open .chevron {
    transform: rotate(0deg);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.employee-row {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    border-bottom: 1px solid #e5e7eb;
    background: white;
}

.employee-row:hover {
    background: #f9fafb;
}

.employee-info {
    padding: 1rem;
    border-right: 1px solid #e5e7eb;
    background: white;
}

.employee-name {
    font-weight: 500;
    color: #111827;
}

.employee-dept {
    font-size: 0.875rem;
    color: #6b7280;
}

.status-cell {
    padding: 0.5rem;
    border-right: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
}

.absence-box {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    text-align: center;
}

.absence-title {
    font-size: 0.75rem;
    font-weight: 500;
}

.absence-hours {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 0.25rem;
}

.no-absence {
    text-align: center;
    color: #5cb85c;
    font-size: 1.25rem;
}

.absence-sick {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

.absence-vacation {
    background: #dbeafe;
    color: #1e3a8a;
    border: 1px solid #bfdbfe;
}

.absence-training {
    background: #ede9fe;
    color: #4c1d95;
    border: 1px solid #ddd6fe;
}

.absence-other {
    background: #fef9c3;
    color: #713f12;
    border: 1px solid #fef08a;
}

.legend {
    margin-top: 1.5rem;
    background: white;
    padding: 1rem;
}

.legend h3 {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.75rem;
}

.legend-items {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.legend-color {
    width: 1rem;
    height: 1rem;
    border-radius: 0.25rem;
}

.legend-color.sick {
    background: #fee2e2;
}

.legend-color.vacation {
    background: #dbeafe;
}

.legend-color.training {
    background: #ede9fe;
}

.legend-color.other {
    background: #fef9c3;
}

.legend-dot {
    width: 0.5rem;
    height: 0.5rem;
    background: #5cb85c;
    border-radius: 50%;
}
</style>